/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { STUNDEN_MAX } from '@/domain/dienstplan/features/VisualEditor/constants';
import { useDeleteBelegung } from '@/domain/dienstplan/features/VisualEditor/hooks/useBelegungen/useDeleteBelegung';
import { useUpdateBelegung } from '@/domain/dienstplan/features/VisualEditor/hooks/useBelegungen/useUpdateBelegungen';
import { TimeInput } from '@/domain/dienstplan/shared/components/TimeInput/TimeInput';
import {
  Belegung,
  BelegungsIntervall,
} from '@/domain/dienstplan/shared/types/Belegung';
import { getColorsForMarkierung } from '@/domain/dienstplan/shared/utils/getColorForMarkierung';
import { getLocaleWeekdayString } from '@/domain/dienstplan/shared/utils/getLocaleWeekdayString';
import { toast } from '@/hooks/use-toast';
import { faPenToSquare, faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import { faCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CSSProperties, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { VisualEditorState } from '@/domain/dienstplan/features/VisualEditor/store';
import { transformTimeStringToMinutes } from '@/domain/dienstplan/features/VisualEditor/utils/timeHelper/transformTimeStringToMinutes';
import { formatHourDecimalToTimeString } from '@/domain/dienstplan/features/VisualEditor/utils/timeHelper/formatHourDecimalToTimeString';
import { transformTimeStringToDecimal } from '@/domain/dienstplan/features/VisualEditor/utils/timeHelper/transformTimeStringToDecimal';
import { Input } from '@/components/ui/input';
import { useVisualEditorContext } from '@/domain/dienstplan/features/VisualEditor/VisualEditorContext';

type StyleProps = CSSProperties & {
  '--dot-color': CSSProperties['color'];
};

type BelegungBeginnEnde = {
  beginn: string;
  ende: string;
};

type ValidationError = {
  field: 'beginn' | 'ende' | 'anzahl';
  message: string;
};

function calculateDauer(beginnTimeString: string, endeTimeString: string) {
  const beginn = transformTimeStringToMinutes(beginnTimeString);
  const ende = transformTimeStringToMinutes(endeTimeString);
  if (ende !== 0) {
    return ende - beginn;
  } else {
    return 24 * 60 - beginn;
  }
}

export function BelegungPopover({
  triggerStyle,
  popoverRect,
  setIsPopoverRect,
  store,
  rects,
  open,
  setOpen,
}: {
  triggerStyle: CSSProperties;
  popoverRect: Belegung;
  setIsPopoverRect: (belegung: Belegung | undefined) => void;
  store: VisualEditorState;
  rects: BelegungsIntervall[];
  open: boolean;
  setOpen: (state: boolean) => void;
}) {
  const { t } = useTranslation();
  const { maxMitarbeiterAnzahl } = useVisualEditorContext();
  const [deleteBelegung] = useDeleteBelegung();
  const [updatedBelegung] = useUpdateBelegung();
  const offset = store.ui.xAxisOffset;
  const anzahlRef = useRef<HTMLInputElement>(null);

  const wochentagLabel = getLocaleWeekdayString(popoverRect.tag);
  const [belegungBeginnEnde, setBelegungBeginnEnde] = useState<
    BelegungBeginnEnde | undefined
  >({
    beginn: formatHourDecimalToTimeString(popoverRect.start, offset),
    ende: formatHourDecimalToTimeString(popoverRect.ende, offset),
  });
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>(
    [],
  );

  function reset() {
    setIsPopoverRect(undefined);
    setBelegungBeginnEnde(undefined);
    setValidationErrors([]);
  }

  function handleDelete() {
    deleteBelegung({ belegungId: popoverRect.id }).then((result) => {
      if (result.data.dienstplan_deleteBelegung) {
        store.removeBelegungById(popoverRect.id);
        toast({
          title: t(
            'domain.dienstplan.mindestbelegung.visualEditor.popover.deleteSuccess',
          ),
        });
      }
    });
    reset();
  }

  // eslint-disable-next-line complexity
  function validate({
    beginn,
    ende,
    anzahl,
  }: {
    beginn: number;
    ende: number;
    anzahl: number;
  }) {
    const validationErrors: ValidationError[] = [];

    if (ende <= beginn && ende !== 0) {
      validationErrors.push({
        field: 'ende',
        message: t(
          'domain.dienstplan.mindestbelegung.visualEditor.popover.validationErrors.endeVorBeginn',
        ),
      });
    }

    if (ende > STUNDEN_MAX) {
      validationErrors.push({
        field: 'ende',
        message: t(
          'domain.dienstplan.mindestbelegung.visualEditor.popover.validationErrors.endeNachTageswechsel',
        ),
      });
    }

    if (anzahl < 1) {
      validationErrors.push({
        field: 'anzahl',
        message: t(
          'domain.dienstplan.mindestbelegung.visualEditor.popover.validationErrors.anzahlMitarbeitende',
        ),
      });
    }
    if (anzahl > maxMitarbeiterAnzahl.value) {
      validationErrors.push({
        field: 'anzahl',
        message: t(
          'domain.dienstplan.mindestbelegung.visualEditor.popover.validationErrors.anzahlMitarbeitendeZuHoch',
          { count: maxMitarbeiterAnzahl.value },
        ),
      });
    }

    const isBeginnIntersectBelegung =
      rects.filter(
        (interval) =>
          interval.belegung.ende <= popoverRect.start &&
          beginn < interval.belegung.ende &&
          popoverRect.id !== interval.belegung.id &&
          popoverRect.arbeitsbereich.id === interval.belegung.arbeitsbereich.id,
      ).length > 0;

    if (isBeginnIntersectBelegung) {
      validationErrors.push({
        field: 'beginn',
        message: t(
          'domain.dienstplan.mindestbelegung.visualEditor.popover.validationErrors.beginn',
        ),
      });
    }

    const isEndeIntersectBelegung =
      rects.filter(
        (interval) =>
          interval.belegung.start >= popoverRect.ende &&
          ende > interval.belegung.start &&
          popoverRect.id !== interval.belegung.id &&
          popoverRect.id !== interval.belegung.id &&
          popoverRect.arbeitsbereich.id === interval.belegung.arbeitsbereich.id,
      ).length > 0;

    if (isEndeIntersectBelegung) {
      validationErrors.push({
        field: 'ende',
        message: t(
          'domain.dienstplan.mindestbelegung.visualEditor.popover.validationErrors.ende',
        ),
      });
    }

    return validationErrors;
  }

  function handleUpdate() {
    const beginn = transformTimeStringToDecimal(
      belegungBeginnEnde.beginn,
      offset,
    );
    const ende = transformTimeStringToDecimal(belegungBeginnEnde.ende, offset);
    const anzahlMitarbeiter = anzahlRef.current.valueAsNumber;

    const validationErrors = validate({
      beginn: beginn,
      ende,
      anzahl: anzahlMitarbeiter,
    });

    if (validationErrors.length === 0) {
      updatedBelegung({
        input: {
          id: popoverRect.id,
          von: formatHourDecimalToTimeString(beginn - offset, offset),
          anzahlMitarbeiter: anzahlMitarbeiter,
          dauer: calculateDauer(
            belegungBeginnEnde.beginn,
            belegungBeginnEnde.ende,
          ),
        },
      }).then(() => {
        const newBelegung = new Belegung({
          ...popoverRect,
          ende,
          start: beginn,
          personenanzahlMax: anzahlMitarbeiter,
        });
        const newInterval = new BelegungsIntervall(newBelegung, 'inactive');
        store.updateBelegungById(newInterval, newBelegung.id);

        reset();
        setOpen(false);
        toast({
          title: t(
            'domain.dienstplan.mindestbelegung.visualEditor.popover.updateSuccess',
          ),
        });
      });
    } else {
      setValidationErrors(validationErrors);
    }
  }

  function handleTimeInputBlur(field: 'beginn' | 'ende', value: string) {
    const updatedBelegung = {
      ...belegungBeginnEnde,
      [field]: value,
    };

    const beginn = transformTimeStringToDecimal(updatedBelegung.beginn, offset);
    const ende = transformTimeStringToDecimal(updatedBelegung.ende, offset);

    const validationErrors = validate({
      beginn: beginn,
      ende,
      anzahl: anzahlRef.current.valueAsNumber,
    });
    setValidationErrors(validationErrors);
    setBelegungBeginnEnde(updatedBelegung);
  }

  function handleAnzahlMitarbeiterChange(value: number) {
    if (value > 0) {
      setValidationErrors((errors) => {
        return errors.filter((e) => e.field !== 'anzahl');
      });
    }
  }

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild className="react-popover-trigger">
        <div
          style={triggerStyle}
          className="bg-neutrals-N100 shadow-lg flex gap-2 rounded-md p-2"
          role="group"
          aria-label="Belegung actions">
          <Button
            variant="icon"
            className=" text-primary-blue-B700"
            aria-label="Edit-Popover">
            <FontAwesomeIcon icon={faPenToSquare} />
          </Button>
          <Button
            aria-label="Delete-Action"
            variant="ghost"
            onClick={(event) => {
              event.stopPropagation();
              handleDelete();
            }}>
            <FontAwesomeIcon
              icon={faTrashCan}
              className="text-primary-red-R500"
            />
          </Button>
        </div>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="center"
        className="w-80 bg-white p-5 flex flex-col gap-5 belegung-popover-content">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <FontAwesomeIcon
              icon={faCircle}
              className={`size-4 text-[var(--dot-color)]`}
              style={
                {
                  '--dot-color': getColorsForMarkierung(
                    popoverRect.arbeitsbereich.markierung,
                  ).base,
                } as StyleProps
              }
            />
            <div className="text-xl font-bold">
              {popoverRect.arbeitsbereich.name}
            </div>
          </div>
          <div className="text-sm text-neutrals-N800">{`${wochentagLabel} • ${
            popoverRect.personenanzahlMax
          } ${t(
            'domain.dienstplan.mindestbelegung.visualEditor.popover.mitarbeitende',
          )}`}</div>
        </div>
        <div className="flex gap-4">
          <div className="flex flex-col gap-1">
            <Label htmlFor="dienstplan_belegungVon">
              {t('domain.dienstplan.mindestbelegung.visualEditor.labels.von')}
            </Label>
            <TimeInput
              autoFocus={false}
              id="dienstplan_belegungVon"
              defaultValue={belegungBeginnEnde.beginn}
              onBlur={(e) =>
                handleTimeInputBlur('beginn', e.currentTarget.value)
              }
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="dienstplan_belegungBis">
              {t('domain.dienstplan.mindestbelegung.visualEditor.labels.bis')}
            </Label>
            <TimeInput
              autoFocus={false}
              id="dienstplan_belegungBis"
              defaultValue={belegungBeginnEnde.ende}
              onBlur={(e) => handleTimeInputBlur('ende', e.currentTarget.value)}
            />
          </div>
        </div>
        <div>
          <Label htmlFor="dienstplan_belegungPersonen">
            {t(
              'domain.dienstplan.mindestbelegung.visualEditor.labels.anzahlMitarbeitende',
            )}
          </Label>
          <Input
            id="dienstplan_belegungPersonen"
            defaultValue={popoverRect.personenanzahlMax}
            ref={anzahlRef}
            type="number"
            onChange={() => {
              handleAnzahlMitarbeiterChange(anzahlRef.current.valueAsNumber);
            }}
          />
        </div>
        {validationErrors.map((validationError) => (
          <div
            key={validationError.field}
            className="text-sm font-semibold text-primary-red-R500">
            {validationError.message}
          </div>
        ))}
        <div className="flex items-center justify-end">
          <Button onClick={handleUpdate} disabled={validationErrors.length > 0}>
            Fertig
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
